/**
 * Zero-install browser client served by `okra serve` / `okra web`.
 *
 * Keep this dependency-free and same-origin: the published CLI can start one
 * process, expose one HTTP+SSE protocol, and render the same document-first
 * workspace on localhost, a LAN address, or behind an HTTPS reverse proxy.
 */
export const WORKBENCH_INDEX_HTML = String.raw`<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <meta name="color-scheme" content="light dark" />
  <title>Local workbench</title>
  <style>
    :root {
      --bg: #f4f2ec;
      --panel: rgba(255,255,255,.78);
      --panel-solid: #fbfaf7;
      --ink: #20231f;
      --muted: #696e67;
      --line: rgba(32,35,31,.12);
      --accent: #1F7A4C; /* brand --primary light — packages/design-system/tokens.json */
      --accent-soft: #dfeadf;
      --danger: #9e3d34;
      --shadow: 0 18px 50px rgba(34, 43, 35, .10);
      --radius: 14px;
      font-family: Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }
    * { box-sizing: border-box; }
    html, body { height: 100%; }
    body { margin: 0; overflow: hidden; color: var(--ink); background: var(--bg); }
    button, select, input { font: inherit; }
    button { color: inherit; }
    .titlebar {
      height: 52px; display: grid; grid-template-columns: 240px minmax(0,1fr) 240px;
      align-items: center; border-bottom: 1px solid var(--line); background: var(--panel-solid);
      padding: 0 12px; position: relative; z-index: 4;
    }
    .brand { display: flex; align-items: center; gap: 8px; font-weight: 720; letter-spacing: -.02em; }
    .brand-mark { width: 22px; height: 22px; border-radius: 7px 7px 11px 7px; background: var(--accent); box-shadow: inset 0 0 0 1px rgba(255,255,255,.25); }
    .brand small { border: 1px solid var(--line); border-radius: 999px; color: var(--muted); font: 600 9px/1 ui-monospace, SFMono-Regular, Menlo, monospace; letter-spacing: .09em; padding: 4px 6px; text-transform: uppercase; }
    .document-title { min-width: 0; text-align: center; font-size: 13px; font-weight: 640; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .title-actions { display: flex; justify-content: flex-end; align-items: center; gap: 7px; }
    .status-dot { width: 7px; height: 7px; border-radius: 50%; background: #9ca39b; }
    .status-dot.online { background: #3b8d57; box-shadow: 0 0 0 4px rgba(59,141,87,.12); }
    .status-label { color: var(--muted); font-size: 11px; }
    .button {
      min-height: 32px; border: 1px solid var(--line); border-radius: 9px; background: var(--panel-solid);
      padding: 0 11px; cursor: pointer; font-size: 12px; font-weight: 620;
    }
    .button:hover { border-color: rgba(40,97,63,.42); }
    .button:focus-visible, select:focus-visible, input:focus-visible { outline: 3px solid rgba(40,97,63,.22); outline-offset: 1px; }
    .button.primary { background: var(--accent); color: white; border-color: var(--accent); }
    .button:disabled { opacity: .48; cursor: not-allowed; }
    .workspace { height: calc(100% - 52px); display: grid; grid-template-columns: 240px minmax(320px,1fr) 312px; }
    .drawer { min-width: 0; overflow: auto; background: var(--panel-solid); }
    .drawer.left { border-right: 1px solid var(--line); }
    .drawer.right { border-left: 1px solid var(--line); }
    .drawer-head { position: sticky; top: 0; z-index: 2; background: var(--panel-solid); padding: 18px 16px 12px; border-bottom: 1px solid var(--line); }
    .eyebrow { color: var(--muted); font: 650 10px/1.2 ui-monospace, SFMono-Regular, Menlo, monospace; letter-spacing: .1em; text-transform: uppercase; }
    h1, h2, p { margin: 0; }
    h2 { margin-top: 6px; font-size: 17px; letter-spacing: -.02em; }
    .upload { width: 100%; margin-top: 14px; }
    .doc-list { padding: 8px; display: grid; gap: 3px; }
    .doc-row { width: 100%; border: 1px solid transparent; border-radius: 10px; background: transparent; padding: 10px; cursor: pointer; text-align: left; }
    .doc-row:hover { background: rgba(40,97,63,.05); }
    .doc-row.active { background: var(--accent-soft); border-color: rgba(40,97,63,.16); }
    .doc-name { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12px; font-weight: 650; }
    .doc-meta { display: block; margin-top: 4px; color: var(--muted); font-size: 10px; }
    .empty-list { color: var(--muted); font-size: 12px; line-height: 1.55; padding: 20px 12px; }
    .reader { min-width: 0; position: relative; background: radial-gradient(circle at 50% 0%, #fbfaf7 0, #ece9e1 58%, #e5e1d8 100%); overflow: hidden; }
    .reader-empty { position: absolute; inset: 0; display: grid; place-items: center; padding: 28px; }
    .empty-card { max-width: 240px; border: 1px solid rgba(255,255,255,.8); border-radius: 22px; background: rgba(255,255,255,.58); box-shadow: var(--shadow); padding: 28px 24px; text-align: center; backdrop-filter: blur(14px); }
    .empty-card .leaf { width: 36px; height: 36px; margin: 0 auto 14px; border-radius: 12px 12px 18px 12px; background: var(--accent); transform: rotate(-6deg); }
    .empty-card h1 { font-size: 15px; font-weight: 650; letter-spacing: -.02em; }
    .empty-card .button { margin-top: 16px; }
    .pdf-frame { width: 100%; height: 100%; border: 0; display: none; background: #777; }
    .panel { padding: 16px; }
    .field { display: grid; gap: 7px; margin-bottom: 14px; }
    .field label { color: var(--muted); font-size: 11px; font-weight: 630; }
    select { width: 100%; height: 36px; border: 1px solid var(--line); border-radius: 9px; background: var(--panel-solid); color: var(--ink); padding: 0 10px; }
    .parse { width: 100%; min-height: 38px; }
    .run-card { margin-top: 15px; border: 1px solid var(--line); border-radius: 12px; background: var(--panel); padding: 12px; }
    .run-line { display: flex; justify-content: space-between; gap: 12px; font-size: 11px; }
    .run-state { color: var(--accent); font-weight: 700; }
    .run-detail { margin-top: 8px; color: var(--muted); font-size: 11px; line-height: 1.45; }
    .output { margin-top: 14px; }
    .output-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 7px; }
    .markdown { max-height: 42vh; overflow: auto; margin: 0; border: 1px solid var(--line); border-radius: 10px; background: rgba(0,0,0,.025); padding: 11px; color: var(--muted); font: 11px/1.55 ui-monospace, SFMono-Regular, Menlo, monospace; white-space: pre-wrap; overflow-wrap: anywhere; }
    .error { color: var(--danger); }
    .mobile-tabs { display: none; }
    @media (max-width: 860px) {
      body { overflow: auto; }
      .titlebar { grid-template-columns: 1fr auto; }
      .document-title { display: none; }
      .status-label { display: none; }
      .workspace { height: auto; min-height: calc(100dvh - 52px); grid-template-columns: 1fr; }
      .drawer { display: none; min-height: calc(100dvh - 52px); border: 0 !important; }
      .reader { min-height: calc(100dvh - 100px); }
      .workspace[data-mobile="workspace"] .left,
      .workspace[data-mobile="reader"] .reader,
      .workspace[data-mobile="extract"] .right { display: block; }
      .mobile-tabs { position: fixed; z-index: 8; left: 50%; bottom: 12px; transform: translateX(-50%); display: flex; gap: 3px; border: 1px solid var(--line); border-radius: 12px; background: var(--panel-solid); box-shadow: var(--shadow); padding: 4px; }
      .mobile-tabs button { min-height: 32px; border: 0; border-radius: 8px; background: transparent; padding: 0 12px; font-size: 11px; }
      .mobile-tabs button.active { background: var(--accent-soft); color: var(--accent); font-weight: 700; }
    }
    @media (prefers-color-scheme: dark) {
      :root { --bg: #171a17; --panel: rgba(30,34,30,.82); --panel-solid: #1e221e; --ink: #edf0ea; --muted: #a4aba3; --line: rgba(237,240,234,.11); --accent: #5BB47E; /* brand --primary dark — packages/design-system/tokens.json */ --accent-soft: #273d2e; --shadow: 0 18px 50px rgba(0,0,0,.3); }
      .reader { background: radial-gradient(circle at 50% 0%, #292e29 0, #1b1e1b 64%, #151715 100%); }
      .empty-card { border-color: rgba(255,255,255,.08); background: rgba(32,36,32,.66); }
    }
  </style>
</head>
<body>
  <header class="titlebar">
    <div class="brand"><span class="brand-mark" role="img" aria-label="okraPDF"></span><small>serve</small></div>
    <div id="document-title" class="document-title">Local workbench</div>
    <div class="title-actions">
      <span id="status-dot" class="status-dot" aria-hidden="true"></span>
      <span id="status-label" class="status-label">Connecting</span>
      <button id="copy-address" class="button" type="button">Copy address</button>
    </div>
  </header>
  <div id="workspace" class="workspace" data-mobile="reader">
    <aside class="drawer left" aria-label="Workspace documents">
      <div class="drawer-head">
        <div class="eyebrow">Workspace</div>
        <h2>Documents</h2>
        <button id="upload-button" class="button primary upload" type="button">Open a PDF</button>
        <input id="file-input" type="file" accept="application/pdf,.pdf" hidden />
      </div>
      <div id="document-list" class="doc-list"><div class="empty-list">No documents</div></div>
    </aside>
    <main class="reader" aria-label="PDF reader">
      <div id="reader-empty" class="reader-empty">
        <div class="empty-card">
          <div class="leaf" aria-hidden="true"></div>
          <h1>No PDF open</h1>
          <button id="empty-upload-button" class="button primary" type="button">Choose PDF</button>
        </div>
      </div>
      <iframe id="pdf-frame" class="pdf-frame" title="Selected PDF"></iframe>
    </main>
    <aside class="drawer right" aria-label="Extract document">
      <div class="drawer-head"><div class="eyebrow">Extract</div><h2>Parse</h2></div>
      <div class="panel">
        <div class="field">
          <label for="parser-select">Parser</label>
          <select id="parser-select"><option value="text-layer">Local text + OCR</option></select>
        </div>
        <button id="parse-button" class="button primary parse" type="button" disabled>Parse</button>
        <div class="run-card" aria-live="polite">
          <div class="run-line"><span>Run</span><span id="run-state" class="run-state">Idle</span></div>
          <div id="run-detail" class="run-detail">Select a PDF</div>
        </div>
        <div class="output">
          <div class="output-head"><span class="eyebrow">Markdown</span><button id="copy-markdown" class="button" type="button" disabled>Copy</button></div>
          <pre id="markdown-output" class="markdown">No parse output yet.</pre>
        </div>
      </div>
    </aside>
  </div>
  <nav class="mobile-tabs" aria-label="Workbench panes">
    <button type="button" data-pane="workspace">Workspace</button>
    <button class="active" type="button" data-pane="reader">Reader</button>
    <button type="button" data-pane="extract">Extract</button>
  </nav>
  <script>
    (function () {
      var state = { documents: [], selectedId: null, markdown: '', activeRunId: null };
      var byId = function (id) { return document.getElementById(id); };
      var workspace = byId('workspace');
      var fileInput = byId('file-input');
      var list = byId('document-list');
      var frame = byId('pdf-frame');
      var readerEmpty = byId('reader-empty');
      var parseButton = byId('parse-button');
      var parserSelect = byId('parser-select');
      var runState = byId('run-state');
      var runDetail = byId('run-detail');
      var markdownOutput = byId('markdown-output');
      var copyMarkdown = byId('copy-markdown');

      async function request(path, options) {
        var response = await fetch(path, options || {});
        var text = await response.text();
        var body = text ? JSON.parse(text) : null;
        if (!response.ok) throw new Error((body && (body.message || body.error)) || ('HTTP ' + response.status));
        return body;
      }

      function setConnection(ok, label) {
        byId('status-dot').classList.toggle('online', ok);
        byId('status-label').textContent = label;
      }

      function selectedDocument() {
        return state.documents.find(function (item) { return item.id === state.selectedId; }) || null;
      }

      function renderDocuments() {
        if (!state.documents.length) {
          list.innerHTML = '<div class="empty-list">No documents</div>';
          return;
        }
        list.innerHTML = '';
        state.documents.forEach(function (item) {
          var row = document.createElement('button');
          row.type = 'button';
          row.className = 'doc-row' + (item.id === state.selectedId ? ' active' : '');
          row.innerHTML = '<span class="doc-name"></span><span class="doc-meta"></span>';
          row.querySelector('.doc-name').textContent = item.title;
          row.querySelector('.doc-meta').textContent = item.pageCount + (item.pageCount === 1 ? ' page' : ' pages');
          row.addEventListener('click', function () { selectDocument(item.id); });
          list.appendChild(row);
        });
      }

      function setRun(status, detail, isError) {
        runState.textContent = status;
        runState.classList.toggle('error', Boolean(isError));
        runDetail.textContent = detail;
      }

      function showArtifacts(artifacts) {
        state.markdown = artifacts && artifacts.markdown ? artifacts.markdown : '';
        markdownOutput.textContent = state.markdown || 'The parser returned no markdown.';
        copyMarkdown.disabled = !state.markdown;
      }

      function selectDocument(id) {
        state.selectedId = id;
        var item = selectedDocument();
        renderDocuments();
        parseButton.disabled = !item;
        byId('document-title').textContent = item ? item.title : 'Local workbench';
        if (item) {
          readerEmpty.style.display = 'none';
          frame.style.display = 'block';
          frame.src = '/document/' + encodeURIComponent(item.id) + '/download';
          location.hash = '#/document/' + encodeURIComponent(item.id);
          setRun('Idle', 'Ready to parse', false);
          workspace.dataset.mobile = 'reader';
          syncMobileTabs();
        }
      }

      async function loadDocuments() {
        var body = await request('/document');
        state.documents = body.data || [];
        var match = location.hash.match(/^#\/document\/([^/]+)$/);
        var requested = match ? decodeURIComponent(match[1]) : null;
        var selected = requested && state.documents.some(function (item) { return item.id === requested; })
          ? requested
          : (state.selectedId || (state.documents[0] && state.documents[0].id));
        if (selected) selectDocument(selected); else renderDocuments();
      }

      async function upload(file) {
        if (!file) return;
        setRun('Opening', file.name, false);
        try {
          var documentRecord = await request('/document', {
            method: 'POST',
            headers: { 'content-type': 'application/pdf', 'x-file-name': encodeURIComponent(file.name) },
            body: file
          });
          await loadDocuments();
          selectDocument(documentRecord.id);
          setRun('Idle', 'Ready to parse', false);
        } catch (error) {
          setRun('Error', error.message || String(error), true);
        } finally {
          fileInput.value = '';
        }
      }

      async function parseSelected() {
        var item = selectedDocument();
        if (!item) return;
        parseButton.disabled = true;
        setRun('Running', 'Parsing…', false);
        try {
          var run = await request('/document/' + encodeURIComponent(item.id) + '/parse', {
            method: 'POST', headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ parserId: parserSelect.value })
          });
          state.activeRunId = run.id;
          if (run.status === 'succeeded') {
            var artifacts = await request('/run/' + encodeURIComponent(run.id) + '/artifacts');
            showArtifacts(artifacts);
            setRun('Complete', run.pageCount + ' page(s) parsed.', false);
          } else if (run.status === 'failed') {
            var failed = await request('/run/' + encodeURIComponent(run.id));
            setRun('Failed', failed.error || 'The parser failed.', true);
          } else {
            setRun(run.status, 'Parsing…', false);
          }
        } catch (error) {
          setRun('Error', error.message || String(error), true);
        } finally {
          parseButton.disabled = !selectedDocument();
        }
      }

      async function loadCatalogs() {
        var body = await request('/parser');
        var parsers = body.data || [];
        if (!parsers.length) return;
        parserSelect.innerHTML = '';
        parsers.forEach(function (parser) {
          var option = document.createElement('option');
          option.value = parser.id;
          option.textContent = parser.displayName;
          parserSelect.appendChild(option);
        });
      }

      function connectEvents() {
        var events = new EventSource('/global/event');
        events.onopen = function () { setConnection(true, 'Connected'); };
        events.onerror = function () { setConnection(false, 'Reconnecting'); };
        function handleEvent(message) {
          try {
            var event = JSON.parse(message.data);
            if (event.type === 'document.opened') loadDocuments().catch(function () {});
            if (event.type === 'run.started' && event.run.documentId === state.selectedId) {
              state.activeRunId = event.run.id;
              setRun('Running', 'Parsing…', false);
            }
            if (event.type === 'run.completed' && event.run.documentId === state.selectedId) {
              showArtifacts(event.artifacts);
              setRun('Complete', event.run.pageCount + ' page(s) parsed.', false);
            }
            if (event.type === 'run.failed' && event.runId === state.activeRunId) {
              setRun('Failed', event.message, true);
            }
          } catch (_) {}
        }
        events.onmessage = handleEvent;
        [
          'server.connected', 'document.opened', 'run.started', 'run.page',
          'run.completed', 'run.failed', 'run.cancelled', 'provider.setup'
        ].forEach(function (type) { events.addEventListener(type, handleEvent); });
      }

      function syncMobileTabs() {
        document.querySelectorAll('[data-pane]').forEach(function (button) {
          button.classList.toggle('active', button.dataset.pane === workspace.dataset.mobile);
        });
      }

      [byId('upload-button'), byId('empty-upload-button')].forEach(function (button) {
        button.addEventListener('click', function () { fileInput.click(); });
      });
      fileInput.addEventListener('change', function () { upload(fileInput.files && fileInput.files[0]); });
      parseButton.addEventListener('click', parseSelected);
      byId('copy-address').addEventListener('click', async function () {
        await navigator.clipboard.writeText(location.href);
        byId('copy-address').textContent = 'Copied';
        setTimeout(function () { byId('copy-address').textContent = 'Copy address'; }, 1200);
      });
      copyMarkdown.addEventListener('click', async function () { await navigator.clipboard.writeText(state.markdown); });
      document.querySelectorAll('[data-pane]').forEach(function (button) {
        button.addEventListener('click', function () { workspace.dataset.mobile = button.dataset.pane; syncMobileTabs(); });
      });
      window.addEventListener('hashchange', function () { loadDocuments().catch(function () {}); });

      Promise.all([
        request('/global/health').then(function (health) { setConnection(health.healthy, health.host === 'cli_serve' ? 'Local server' : 'Connected'); }),
        loadCatalogs(),
        loadDocuments()
      ]).then(connectEvents).catch(function (error) {
        setConnection(false, 'Offline');
        setRun('Error', error.message || String(error), true);
      });
    }());
  </script>
</body>
</html>`;
